import { BannerId, SaveInternalBannersResult } from '../typings/banners';
import { ValidationResultItem } from '../typings/common';
import { CurrencyCode } from '../typings/graphql-grid';
import {
    InternalBannersBaseInfo,
    InternalBannersEditData,
    InternalPlaceTemplates,
    InternalResourceId,
    InternalTemplateResources
} from '../typings/internalBanners';
import { CopyInternalBannersInputData } from '../typings/internalBannersEditState';

export const GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION =
    'GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION';
export const DELETE_INTERNAL_BANNERS = 'DELETE_INTERNAL_BANNERS';
export const CLEAR_INTERNAL_BANNERS = 'CLEAR_INTERNAL_BANNERS';
export const RESTORE_INTERNAL_BANNERS = 'RESTORE_INTERNAL_BANNERS';
export const COPY_INTERNAL_BANNER = 'COPY_INTERNAL_BANNER';
export const UPDATE_INTERNAL_BANNERS_FOR_EDIT =
    'UPDATE_INTERNAL_BANNERS_FOR_EDIT';
export const UPDATE_INTERNAL_BANNER_BASE_INFO =
    'UPDATE_INTERNAL_BANNER_BASE_INFO';
export const SAVE_INTERNAL_BANNERS = 'SAVE_INTERNAL_BANNERS';
export const SAVE_INTERNAL_BANNERS_SUCCESS = 'SAVE_INTERNAL_BANNERS_SUCCESS';
export const UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES =
    'UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES';
export const UPLOAD_INTERNAL_BANNER_IMAGE = 'UPLOAD_INTERNAL_BANNER_IMAGE';
export const UPDATE_INTERNAL_BANNER_IMAGE = 'UPDATE_INTERNAL_BANNER_IMAGE';
export const ADD_NEW_INTERNAL_BANNERS = 'ADD_NEW_INTERNAL_BANNERS';
export const EDIT_INTERNAL_BANNER = 'EDIT_INTERNAL_BANNER';
export const GET_INTERNAL_BANNER_FOR_CREATION =
    'GET_INTERNAL_BANNER_FOR_CREATION';
export const SAVE_INTERNAL_BANNER_NAME_EDITOR_CHANGES =
    'SAVE_INTERNAL_BANNER_NAME_EDITOR_CHANGES';

export type GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION = typeof GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION;
export type DELETE_INTERNAL_BANNERS = typeof DELETE_INTERNAL_BANNERS;
export type CLEAR_INTERNAL_BANNERS = typeof CLEAR_INTERNAL_BANNERS;
export type RESTORE_INTERNAL_BANNERS = typeof RESTORE_INTERNAL_BANNERS;
export type COPY_INTERNAL_BANNER = typeof COPY_INTERNAL_BANNER;
export type UPDATE_INTERNAL_BANNERS_FOR_EDIT = typeof UPDATE_INTERNAL_BANNERS_FOR_EDIT;
export type UPDATE_INTERNAL_BANNER_BASE_INFO = typeof UPDATE_INTERNAL_BANNER_BASE_INFO;
export type SAVE_INTERNAL_BANNERS = typeof SAVE_INTERNAL_BANNERS;
export type SAVE_INTERNAL_BANNERS_SUCCESS = typeof SAVE_INTERNAL_BANNERS_SUCCESS;
export type UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES = typeof UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES;
export type UPLOAD_INTERNAL_BANNER_IMAGE = typeof UPLOAD_INTERNAL_BANNER_IMAGE;
export type UPDATE_INTERNAL_BANNER_IMAGE = typeof UPDATE_INTERNAL_BANNER_IMAGE;
export type ADD_NEW_INTERNAL_BANNERS = typeof ADD_NEW_INTERNAL_BANNERS;
export type EDIT_INTERNAL_BANNER = typeof EDIT_INTERNAL_BANNER;
export type GET_INTERNAL_BANNER_FOR_CREATION = typeof GET_INTERNAL_BANNER_FOR_CREATION;
export type SAVE_INTERNAL_BANNER_NAME_EDITOR_CHANGES = typeof SAVE_INTERNAL_BANNER_NAME_EDITOR_CHANGES;

export interface SaveInternalBannerNameEditorChangesAction {
    type: SAVE_INTERNAL_BANNER_NAME_EDITOR_CHANGES;
    bannerId: BannerId;
}

export function saveInternalBannerNameEditorChanges(
    bannerId: BannerId
): SaveInternalBannerNameEditorChangesAction {
    return {
        type: SAVE_INTERNAL_BANNER_NAME_EDITOR_CHANGES,
        bannerId
    };
}

export interface GetInternalBannersForEditOrCreation {
    type: GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION;
    login: string;
}

export function getInternalBannersForEditOrCreation(
    login: string
): GetInternalBannersForEditOrCreation {
    return {
        type: GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION,
        login
    };
}

export interface DeleteInternalBanners {
    type: DELETE_INTERNAL_BANNERS;
    bannersIds: BannerId[];
}

export function deleteInternalBanners(
    bannersIds: BannerId[]
): DeleteInternalBanners {
    return {
        type: DELETE_INTERNAL_BANNERS,
        bannersIds
    };
}

export interface RestoreInternalBanners {
    type: RESTORE_INTERNAL_BANNERS;
    bannersIds: Array<BannerId>;
}

export function restoreInternalBanners(
    bannersIds: Array<BannerId>
): RestoreInternalBanners {
    return {
        type: RESTORE_INTERNAL_BANNERS,
        bannersIds
    };
}

export interface CopyInternalBannerAction {
    type: COPY_INTERNAL_BANNER;
    bannerId: BannerId;
    newBannerId: BannerId;
}

export function copyInternalBanner(
    bannerId: BannerId,
    newBannerId: BannerId
): CopyInternalBannerAction {
    return {
        type: COPY_INTERNAL_BANNER,
        bannerId,
        newBannerId
    };
}
export interface ClearInternalBanners {
    type: CLEAR_INTERNAL_BANNERS;
    bannersIds: BannerId[];
    workCurrency: CurrencyCode;
}

export function clearInternalBanners(
    bannersIds: BannerId[],
    workCurrency: CurrencyCode
): ClearInternalBanners {
    return {
        type: CLEAR_INTERNAL_BANNERS,
        bannersIds,
        workCurrency
    };
}

export interface UpdateInternalBannersForEdit {
    type: UPDATE_INTERNAL_BANNERS_FOR_EDIT;
    data: InternalBannersEditData;
    placeTemplates: InternalPlaceTemplates;
    templateResources: InternalTemplateResources;
}

export function updateInternalBannersForEdit(
    bannersData: InternalBannersEditData,
    placeTemplates: InternalPlaceTemplates,
    templateResources: InternalTemplateResources
): UpdateInternalBannersForEdit {
    return {
        type: UPDATE_INTERNAL_BANNERS_FOR_EDIT,
        data: bannersData,
        placeTemplates: placeTemplates,
        templateResources: templateResources
    };
}

export interface UpdateInternalBannerBaseInfo {
    type: UPDATE_INTERNAL_BANNER_BASE_INFO;
    bannerId: BannerId;
    changed: {
        field: keyof InternalBannersBaseInfo;
        value: string;
    };
}

export function updateInternalBannerBaseInfo(
    bannerId: BannerId,
    changed: {
        field: keyof InternalBannersBaseInfo;
        value: string;
    }
): UpdateInternalBannerBaseInfo {
    return {
        type: UPDATE_INTERNAL_BANNER_BASE_INFO,
        bannerId,
        changed
    };
}

export interface UpdateInternalTemplateResources {
    type: UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES;
    bannerId: BannerId;
    changed: {
        resourceId: InternalResourceId;
        value: string;
    };
}

export function updateInternalTemplateResources(
    bannerId: BannerId,
    changed: {
        resourceId: InternalResourceId;
        value: string;
    }
): UpdateInternalTemplateResources {
    return {
        type: UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES,
        bannerId,
        changed
    };
}

export interface SaveInternalBanners {
    type: SAVE_INTERNAL_BANNERS;
}

export function saveInternalBanners(): SaveInternalBanners {
    return {
        type: SAVE_INTERNAL_BANNERS
    };
}

export interface SaveInternalBannersSuccess {
    type: SAVE_INTERNAL_BANNERS_SUCCESS;
    result: SaveInternalBannersResult;
}

export function saveInternalBannersSuccess(
    result: SaveInternalBannersResult
): SaveInternalBannersSuccess {
    return {
        type: SAVE_INTERNAL_BANNERS_SUCCESS,
        result
    };
}

export interface UploadInternalBannerImage {
    type: UPLOAD_INTERNAL_BANNER_IMAGE;
    bannerId: BannerId;
    resourceId: InternalResourceId;
    imageToUpload: File;
    imageErrors?: ValidationResultItem[];
}

export function uploadInternalBannerImage(
    bannerId: BannerId,
    resourceId: InternalResourceId,
    imageToUpload: File
): UploadInternalBannerImage {
    return {
        type: UPLOAD_INTERNAL_BANNER_IMAGE,
        bannerId,
        resourceId,
        imageToUpload
    };
}

export interface UpdateInternalBannerImage {
    type: UPDATE_INTERNAL_BANNER_IMAGE;
    bannerId: BannerId;
    resourceId: InternalResourceId;
    hash: string;
    imageErrors?: ValidationResultItem[];
}
export function updateInternalBannerImage(
    bannerId: BannerId,
    resourceId: InternalResourceId,
    hash: string,
    imageErrors?: ValidationResultItem[]
): UpdateInternalBannerImage {
    return {
        type: UPDATE_INTERNAL_BANNER_IMAGE,
        bannerId: bannerId,
        resourceId,
        hash,
        imageErrors
    };
}

export interface EditInternalBanner {
    type: EDIT_INTERNAL_BANNER;
    bannerId: BannerId;
}

export function editInternalBanner(bannerId: BannerId): EditInternalBanner {
    return {
        type: EDIT_INTERNAL_BANNER,
        bannerId
    };
}

export interface AddNewInternalBannersAction {
    type: ADD_NEW_INTERNAL_BANNERS;
    banners: CopyInternalBannersInputData[];
}

export function addNewInternalBanners(
    banners: CopyInternalBannersInputData[]
): AddNewInternalBannersAction {
    return {
        type: ADD_NEW_INTERNAL_BANNERS,
        banners
    };
}

export type InternalBannersMultiEditActions =
    | GetInternalBannersForEditOrCreation
    | UpdateInternalBannersForEdit
    | UpdateInternalBannerBaseInfo
    | UpdateInternalTemplateResources
    | SaveInternalBannerNameEditorChangesAction
    | SaveInternalBanners
    | SaveInternalBannersSuccess
    | UploadInternalBannerImage
    | UpdateInternalBannerImage
    | CopyInternalBannerAction
    | AddNewInternalBannersAction
    | EditInternalBanner
    | DeleteInternalBanners
    | RestoreInternalBanners
    | ClearInternalBanners;
